import { SignGroup, SignQueueItem } from "services/cabinet-queues.service";
import { daysSince } from "features/cabinet-tasks/task-row";

/** A document that has waited longer than this many days is «old»: amber in the list, and the «older» toggle shows only those. */
export const OLD_AFTER_DAYS = 30;

export type TypeFilter = "all" | SignGroup;
export type SignatureFilter = "all" | "Click" | "Dokobit";

export interface SignFilters {
  type: TypeFilter;
  /** "" = every company. */
  companyId: string;
  signature: SignatureFilter;
  olderOnly: boolean;
  search: string;
}

// The choices of the «Tipas» and «Parašas» drop-downs. How they are read: `value` is what the
// address holds (`?type=work`, `?sign=Click`) and what is compared with the document; the
// first choice is the default and means «no filter». A value in the address that is not
// listed counts as the default.
export const TYPE_OPTIONS: { value: TypeFilter; labelKey: string }[] = [
  { value: "all", labelKey: "queue_filter_all" },
  { value: "work", labelKey: "queue_type_work" },
  { value: "mine", labelKey: "queue_type_mine" },
  { value: "team", labelKey: "queue_type_team" },
];

export const SIGNATURE_OPTIONS: { value: SignatureFilter; labelKey: string }[] = [
  { value: "all", labelKey: "queue_filter_all" },
  { value: "Click", labelKey: "queue_signature_click" },
  { value: "Dokobit", labelKey: "queue_signature_dokobit" },
];

/** The filters the address holds: `?type=work&company=…&sign=Click&old=1&q=…`. */
export function signFiltersFromQuery(params: URLSearchParams): SignFilters {
  const type = TYPE_OPTIONS.find((option) => option.value === params.get("type"))?.value ?? "all";
  const signature = SIGNATURE_OPTIONS.find((option) => option.value === params.get("sign"))?.value ?? "all";
  return {
    type,
    companyId: params.get("company") ?? "",
    signature,
    olderOnly: params.get("old") === "1",
    search: params.get("q") ?? "",
  };
}

// What the search looks in: the title, the customers and who submitted the document.
const searchableText = (item: SignQueueItem) =>
  [item.title, ...item.customerNames, item.ownerName ?? ""].join("\n").toLowerCase();

/** The documents that pass every filter that is switched on, in their order. */
export function filterSignItems(items: SignQueueItem[], filters: SignFilters): SignQueueItem[] {
  const search = filters.search.trim().toLowerCase();
  return items.filter((item) => {
    if (filters.type !== "all" && item.group !== filters.type) return false;
    if (filters.companyId && item.companyId !== filters.companyId) return false;
    if (filters.signature !== "all" && item.signType !== filters.signature) return false;
    if (filters.olderOnly && daysSince(item.since) <= OLD_AFTER_DAYS) return false;
    if (search && !searchableText(item).includes(search)) return false;
    return true;
  });
}
